export type RoadColour = 'green' | 'yellow' | 'amber' | 'red' | 'black';

export interface ForecastData {
  overallRoadCondition: string;
  roadCondition: string;
  roadTemperature: number | null;
  temperature: number | null;
  windSpeed: number | null;
  windDirection: number | null;
  precipitationType: string;
  precipitationAmount: number | null;
  frictionValue: number | null;
  visibility: boolean;
  time: string;
  type: string;
}

export interface RoadSection {
  id: string;
  description: string;
  roadNumber: number;
  geometry: {
    type: 'LineString';
    coordinates: [number, number][];
  };
  condition: ForecastData | null;
  colour: RoadColour;
}

export interface RoadCamera {
  id: string;
  name: string;
  lat: number;
  lon: number;
  presets: { id: string; imageUrl: string; presentationName: string }[];
}

export interface RoadConditionsData {
  roadSections: RoadSection[];
  cameras: RoadCamera[];
  closures: { id: string; description: string; roadNumber?: number }[];
  lastUpdated: string;
}

export function getRoadColour(forecast: ForecastData | null, isClosed: boolean): RoadColour {
  if (isClosed) return 'black';
  if (!forecast) return 'green';

  const { roadCondition, frictionValue, overallRoadCondition, precipitationType } = forecast;

  // RED - dangerous
  if (roadCondition === 'ICY') return 'red';
  if (frictionValue !== null && frictionValue < 0.3) return 'red';
  if (overallRoadCondition === 'EXTREMELY_POOR') return 'red';

  // AMBER - poor
  if (roadCondition === 'FROST' || roadCondition === 'PARTLY_ICY') return 'amber';
  if (frictionValue !== null && frictionValue < 0.5) return 'amber';
  if (precipitationType === 'SNOW' && frictionValue !== null && frictionValue < 0.5) return 'amber';

  // YELLOW - caution
  if (roadCondition === 'WET' || roadCondition === 'SLUSH') return 'yellow';
  if (roadCondition === 'SNOW') return 'yellow';
  if (precipitationType === 'RAIN' || precipitationType === 'SLEET') return 'yellow';
  if (overallRoadCondition === 'POOR_CONDITION') return 'yellow';

  // GREEN - good
  return 'green';
}

export const ROAD_COLOURS: Record<RoadColour, string> = {
  green: '#22C55E',
  yellow: '#EAB308',
  amber: '#F97316',
  red: '#EF4444',
  black: '#1F2937',
};

export const ROAD_COLOUR_LABELS: Record<RoadColour, string> = {
  green: 'Good driving conditions',
  yellow: 'Caution - wet or light precipitation',
  amber: 'Poor - frost, packed snow, reduced visibility',
  red: 'Dangerous - ice, heavy snow, very low friction',
  black: 'Road closed',
};

export const ROAD_WEIGHTS: Record<number, number> = {
  4: 5,
  21: 5,
  79: 5,
  940: 4,
  956: 4,
};

export const DEFAULT_ROAD_WEIGHT = 3;

export const KEY_LOCATIONS = [
  { name: 'Levi Centre', lat: 67.7997, lon: 24.8127, type: 'ski' as const },
  { name: 'Ylläs (Äkäslompolo)', lat: 67.6048, lon: 24.0084, type: 'ski' as const },
  { name: 'Ylläs (Ylläsjärvi)', lat: 67.5553, lon: 24.2447, type: 'ski' as const },
  { name: 'Kittilä Airport (KTT)', lat: 67.7011, lon: 24.8468, type: 'plane' as const },
  { name: 'Kolari Train Station', lat: 67.3318, lon: 23.7847, type: 'train' as const },
  { name: 'Kittilä Town', lat: 67.6558, lon: 24.9137, type: 'building' as const },
  { name: 'Muonio', lat: 67.9227, lon: 23.6826, type: 'building' as const },
];

export const LAPLAND_CENTRE: [number, number] = [67.65, 24.5];
export const DEFAULT_ZOOM = 9;

export const TILE_URLS = {
  winter: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  summer: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
};

export const KEY_ROUTES = [
  {
    id: 'airport-levi',
    name: 'Kittilä Airport → Levi',
    distance: '15 km',
    time: '~15 min',
    roadNumbers: [79, 956],
  },
  {
    id: 'airport-yllas',
    name: 'Kittilä Airport → Ylläs',
    distance: '55 km',
    time: '~40 min',
    roadNumbers: [79, 940],
  },
  {
    id: 'levi-yllas',
    name: 'Levi ↔ Ylläs',
    distance: '60 km',
    time: '~50 min',
    roadNumbers: [79],
  },
  {
    id: 'kolari-yllas',
    name: 'Kolari Station → Ylläs',
    distance: '37 km',
    time: '~30 min',
    roadNumbers: [21, 940],
  },
];
